import React, { useEffect, useState, useCallback } from 'react';
import Card from '../../components/ui/Card';
import Btn from '../../components/ui/Btn';
import Badge from '../../components/ui/Badge';
import { Select } from '../../components/ui/Input';
import { metasApi, funcionariosApi } from '../../api/rh';
import { COLORS } from '../../theme';
import { useTituloPagina } from '../../context/PageTitleContext';
import NovaMetaModal from './NovaMetaModal';

const MESES = ['', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
  .map((m, i) => i === 0 ? null : { value: String(i), label: m }).filter(Boolean);

const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 5 }, (_, i) => ANO_ATUAL - 3 + i).map(a => ({ value: String(a), label: String(a) }));

const TIPOS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'valor_venda', label: 'Valor de Venda' },
  { value: 'valor_lucro', label: 'Valor de Lucro' }
];

function formatarReais(v) {
  return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function MetasList() {
  useTituloPagina('Metas');
  const hoje = new Date();
  const [mes, setMes] = useState(String(hoje.getMonth() + 1));
  const [ano, setAno] = useState(String(hoje.getFullYear()));
  const [funcionarioId, setFuncionarioId] = useState('');
  const [tipo, setTipo] = useState('');
  const [funcionarios, setFuncionarios] = useState([]);
  const [metas, setMetas] = useState([]);
  const [criando, setCriando] = useState(false);
  const [processandoId, setProcessandoId] = useState(null);
  const [erro, setErro] = useState('');

  useEffect(() => { funcionariosApi.listarAtivosLista().then(setFuncionarios).catch(() => {}); }, []);

  const carregar = useCallback(() => {
    const params = { mes, ano };
    if (funcionarioId) params.funcionario_id = funcionarioId;
    if (tipo) params.tipo = tipo;
    metasApi.listar(params).then(setMetas).catch(() => {});
  }, [mes, ano, funcionarioId, tipo]);

  useEffect(() => { carregar(); }, [carregar]);

  const opcoesFuncionarios = [{ value: '', label: 'Todos os vendedores' }, ...funcionarios.map(f => ({ value: String(f.id), label: f.nome }))];

  const lancarBonificacao = async (id) => {
    setProcessandoId(id);
    setErro('');
    try {
      await metasApi.lancarBonificacao(id);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessandoId(null);
    }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir esta meta?')) return;
    setProcessandoId(id);
    setErro('');
    try {
      await metasApi.excluir(id);
      carregar();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessandoId(null);
    }
  };

  return (
    <div>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 14, borderBottom: `1px solid ${COLORS.border}`, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'end' }}>
            <Select label="Mês" options={MESES} value={mes} onChange={e => setMes(e.target.value)} style={{ width: 150 }} />
            <Select label="Ano" options={ANOS} value={ano} onChange={e => setAno(e.target.value)} style={{ width: 110 }} />
            <Select label="Vendedor" options={opcoesFuncionarios} value={funcionarioId} onChange={e => setFuncionarioId(e.target.value)} style={{ width: 220 }} />
            <Select label="Tipo" options={TIPOS} value={tipo} onChange={e => setTipo(e.target.value)} style={{ width: 180 }} />
          </div>
          <Btn onClick={() => setCriando(true)}>+ Nova Meta</Btn>
        </div>

        {erro && <p style={{ color: COLORS.danger, fontSize: 13, padding: '10px 14px 0' }}>{erro}</p>}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', background: COLORS.bg }}>
                <th style={{ padding: 10 }}>Vendedor</th>
                <th style={{ padding: 10 }}>Tipo</th>
                <th style={{ padding: 10 }}>Meta</th>
                <th style={{ padding: 10 }}>Atingido</th>
                <th style={{ padding: 10 }}>%</th>
                <th style={{ padding: 10 }}>Status</th>
                <th style={{ padding: 10 }}>Bonificação</th>
                <th style={{ padding: 10 }} />
              </tr>
            </thead>
            <tbody>
              {metas.map(m => (
                <tr key={m.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: 10 }}>{m.funcionario_nome || <em style={{ color: COLORS.textMid }}>Meta Geral</em>}</td>
                  <td style={{ padding: 10 }}>{m.tipo_label}</td>
                  <td style={{ padding: 10 }}>{formatarReais(m.valor_meta)}</td>
                  <td style={{ padding: 10 }}>{formatarReais(m.valor_atingido)}</td>
                  <td style={{ padding: 10 }}>{Number(m.percentual_atingido).toFixed(0)}%</td>
                  <td style={{ padding: 10 }}><Badge tom={m.bateu ? 'sucesso' : 'info'}>{m.bateu ? 'Atingida' : 'Em Andamento'}</Badge></td>
                  <td style={{ padding: 10 }}>
                    {Number(m.valor_bonificacao) > 0 ? formatarReais(m.valor_bonificacao) : '-'}
                    {m.ajuste_financeiro_id && <div><Badge tom="sucesso">Lançada</Badge></div>}
                  </td>
                  <td style={{ padding: 10, display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {m.funcionario_id && m.bateu && Number(m.valor_bonificacao) > 0 && !m.ajuste_financeiro_id && (
                      <Btn tamanho="sm" onClick={() => lancarBonificacao(m.id)} disabled={processandoId === m.id}>Lançar Bonificação</Btn>
                    )}
                    {!m.ajuste_financeiro_id && (
                      <Btn tamanho="sm" variante="danger" onClick={() => excluir(m.id)} disabled={processandoId === m.id}>Excluir</Btn>
                    )}
                  </td>
                </tr>
              ))}
              {metas.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: COLORS.textMid }}>Nenhuma meta cadastrada para esse período.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {criando && (
        <NovaMetaModal
          mes={mes} ano={ano} funcionarios={funcionarios}
          onFechar={() => setCriando(false)}
          onCriada={() => { setCriando(false); carregar(); }}
        />
      )}
    </div>
  );
}
